import { useMemo, useState } from 'react';
import { api, downloadFile, WeeklyScorecardRow, DeliverableRow } from '../lib/api';
import { useQuery } from '../hooks/useQuery';
import { useConfig, jiraIssueUrl } from '../hooks/useConfig';
import { fmtCurrency, fmtHours, fmtMonth } from '../lib/fmt';
import {
  Modal, Table, Th, Tr, Td, Spinner, EmptyState, Badge, IssueTypeBadge,
} from './ui';

type TypeFilter = 'all' | 'Deliverable' | 'Overhead';

type Tab = 'monthly' | 'weekly';

/**
 * Drill-down modal for any aggregated team-member number.
 *
 * Two views share a tabbed modal:
 *  - **Monthly**  — ORP's shipped-attribution model. Deliverables whose
 *    completed_date lands in the selected month, grouped by engagement.
 *    This reconciles to the bonus tiers and reports elsewhere in the app.
 *  - **Weekly**   — T&M model used in weekly L10 meetings. Worklogs
 *    bucketed by Mon-Sun week × deliverable. Different semantics — hours
 *    are attributed to the week they were logged, not the month the
 *    deliverable completed.
 */
export function DrilldownModal({
  userId,
  userLabel,
  month,
  metricLabel,
  metricValue,
  kind,
  defaultTab = 'monthly',
  onClose,
}: {
  userId: string;
  userLabel: string;
  month: string; // YYYY-MM
  metricLabel: string;
  metricValue: string;
  /** 'shipped' shows only Completed deliverables; 'wip' only open ones. */
  kind: 'shipped' | 'wip';
  defaultTab?: Tab;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<Tab>(defaultTab);
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');

  return (
    <Modal
      title={`${userLabel} — ${metricLabel}`}
      subtitle={(
        <span>
          {fmtMonth(month)} · <strong>{metricValue}</strong>
        </span>
      )}
      onClose={onClose}
      width={1100}
    >
      <div style={{ display: 'flex', alignItems: 'center', borderBottom: '0.5px solid var(--color-border)' }}>
        <TabButton active={tab === 'monthly'} onClick={() => setTab('monthly')}>
          By month (bonus attribution)
        </TabButton>
        <TabButton active={tab === 'weekly'} onClick={() => setTab('weekly')}>
          By week (hours × rate)
        </TabButton>
        <TypeFilterChips value={typeFilter} onChange={setTypeFilter} />
      </div>
      {tab === 'monthly'
        ? <MonthlyView userId={userId} month={month} kind={kind} typeFilter={typeFilter} />
        : <WeeklyView userId={userId} kind={kind} typeFilter={typeFilter} />}
    </Modal>
  );
}

function TypeFilterChips({ value, onChange }: { value: TypeFilter; onChange: (v: TypeFilter) => void }) {
  const opts: Array<{ k: TypeFilter; label: string }> = [
    { k: 'all',         label: 'All' },
    { k: 'Deliverable', label: 'Deliverables' },
    { k: 'Overhead',    label: 'Overheads' },
  ];
  return (
    <div style={{ marginLeft: 'auto', marginRight: 14, display: 'flex', gap: 4 }}>
      {opts.map((o) => {
        const active = value === o.k;
        return (
          <button
            key={o.k}
            onClick={() => onChange(o.k)}
            style={{
              fontSize: 10,
              padding: '3px 9px',
              borderRadius: 3,
              border: active ? '0.5px solid var(--color-blue)' : '0.5px solid var(--color-border-strong)',
              background: active ? 'var(--color-blue-bg)' : 'var(--color-surface)',
              color: active ? 'var(--color-blue-text)' : 'var(--color-text-muted)',
              cursor: 'pointer',
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: 'none',
        border: 'none',
        borderBottom: active ? '2px solid var(--color-blue)' : '2px solid transparent',
        padding: '10px 20px',
        fontSize: 12,
        fontWeight: 500,
        color: active ? 'var(--color-text)' : 'var(--color-text-muted)',
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

// ─── Monthly view ──────────────────────────────────────────────────────────

function MonthlyView({ userId, month, kind, typeFilter }: {
  userId: string; month: string; kind: 'shipped' | 'wip'; typeFilter: TypeFilter;
}) {
  const { config } = useConfig();
  const statusParam = kind === 'shipped' ? 'Shipped' : 'In Progress';
  const { data, isLoading } = useQuery(
    () => api.scorecard.deliverables(userId, {
      month,
      status: statusParam,
      ...(typeFilter !== 'all' ? { issue_type: typeFilter } : {}),
    }),
    [userId, month, statusParam, typeFilter],
  );

  if (isLoading) return <Spinner />;
  if (!data || !data.deliverables.length) {
    const label = kind === 'shipped' ? 'shipped' : 'open';
    const typeNote = typeFilter !== 'all' ? ` ${typeFilter}s` : '';
    return <EmptyState message={`No ${label}${typeNote} in ${fmtMonth(month)}.`} />;
  }

  // Group by engagement for subtotals. Work items without an engagement go
  // under a synthetic "(no engagement)" bucket at the end.
  const groups = groupByEngagement(data.deliverables);
  const grandTotal = data.deliverables.reduce((s, d) => s + Number(d.shipped_value), 0);

  return (
    <Table>
      <thead>
        <tr>
          <Th>Deliverable</Th>
          <Th>Status</Th>
          <Th right>Hours</Th>
          <Th right>Rate</Th>
          <Th right>Value</Th>
        </tr>
      </thead>
      <tbody>
        {groups.map((g) => (
          <EngagementGroup
            key={g.engagement_jira_issue_id ?? '__none__'}
            label={g.engagement_summary ?? '(no engagement)'}
            subtotal={g.subtotal}
            subtotalLabel="value"
          >
            {g.deliverables.map((d) => {
              const url = jiraIssueUrl(config, d.jira_issue_key);
              return (
                <Tr key={d.jira_issue_id}>
                  <Td bold>
                    <IssueTypeBadge issueType={d.issue_type} />
                    {url ? (
                      <a href={url} target="_blank" rel="noreferrer" style={{ color: 'var(--color-blue)', marginRight: 6 }}>
                        {d.jira_issue_key}
                      </a>
                    ) : (
                      <span style={{ color: 'var(--color-text-muted)', marginRight: 6 }}>{d.jira_issue_key}</span>
                    )}
                    {d.summary}
                  </Td>
                  <Td><Badge variant="green">{d.status}</Badge></Td>
                  <Td right>{fmtHours(Number(d.total_billable_hours))}</Td>
                  <Td right muted>${d.billable_rate}/h</Td>
                  <Td right bold style={{ color: 'var(--color-green)' }}>{fmtCurrency(Number(d.shipped_value))}</Td>
                </Tr>
              );
            })}
          </EngagementGroup>
        ))}
        <tr style={{ borderTop: '0.5px solid var(--color-border-strong)', background: 'var(--color-border)' }}>
          <td colSpan={4} style={{ padding: '9px 16px', textAlign: 'right', fontWeight: 500, fontSize: 12 }}>
            Grand total
          </td>
          <td style={{ padding: '9px 16px', textAlign: 'right', fontWeight: 500, fontSize: 12, color: 'var(--color-green)' }}>
            {fmtCurrency(grandTotal)}
          </td>
        </tr>
      </tbody>
    </Table>
  );
}

function groupByEngagement(rows: DeliverableRow[]) {
  const map = new Map<string, {
    engagement_jira_issue_id: string | null;
    engagement_summary: string | null;
    deliverables: DeliverableRow[];
    subtotal: number;
  }>();
  for (const r of rows) {
    const key = r.parent_engagement_summary ?? '__none__';
    let group = map.get(key);
    if (!group) {
      group = {
        engagement_jira_issue_id: null,
        engagement_summary: r.parent_engagement_summary ?? null,
        deliverables: [],
        subtotal: 0,
      };
      map.set(key, group);
    }
    group.deliverables.push(r);
    group.subtotal += Number(r.shipped_value);
  }
  return Array.from(map.values());
}

function EngagementGroup({
  label, subtotal, subtotalLabel, children,
}: {
  label: string;
  subtotal: number;
  subtotalLabel: string;
  children: React.ReactNode;
}) {
  return (
    <>
      <tr style={{ background: 'var(--color-border)' }}>
        <td colSpan={5} style={{ padding: '7px 16px', fontSize: 11, fontWeight: 500, color: 'var(--color-text-muted)' }}>
          {label}
          <span style={{ float: 'right' }}>
            {subtotalLabel}: <strong>{fmtCurrency(subtotal)}</strong>
          </span>
        </td>
      </tr>
      {children}
    </>
  );
}

// ─── Weekly view ───────────────────────────────────────────────────────────

const WEEKS_OPTIONS = [4, 8, 13, 26];

function WeeklyView({ userId, kind, typeFilter }: {
  userId: string; kind: 'shipped' | 'wip'; typeFilter: TypeFilter;
}) {
  const { config } = useConfig();
  const [weeks, setWeeks] = useState(8);
  const { data, isLoading } = useQuery(
    () => api.scorecard.weekly(userId, { weeks, kind }),
    [userId, weeks, kind],
  );
  const urlFor = (key: string) => jiraIssueUrl(config, key);

  const pivot = useMemo(() => {
    if (!data) return null;
    const filtered = typeFilter === 'all'
      ? data.rows
      : data.rows.filter((r) => r.issue_type === typeFilter);
    return pivotWeekly(filtered, data.week_starts);
  }, [data, typeFilter]);

  return (
    <div>
      <div style={{ padding: '10px 16px', borderBottom: '0.5px solid var(--color-border)', display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: 'var(--color-text-muted)' }}>
        <span>Last</span>
        <select
          value={String(weeks)}
          onChange={(e) => setWeeks(parseInt(e.target.value))}
          style={{ fontSize: 11, padding: '2px 6px', borderRadius: 'var(--radius-sm)', border: '0.5px solid var(--color-border-strong)', background: 'var(--color-surface)' }}
        >
          {WEEKS_OPTIONS.map((w) => <option key={w} value={w}>{w} weeks</option>)}
        </select>
        <span>· Mon–Sun buckets · hours × rate per deliverable</span>
        <button
          onClick={() => {
            const qs = new URLSearchParams({ weeks: String(weeks), kind, format: 'csv' }).toString();
            downloadFile(`/scorecard/${userId}/weekly?${qs}`, `weekly-${userId}-${kind}.csv`);
          }}
          disabled={!data || !data.rows.length}
          style={{ marginLeft: 'auto', fontSize: 11, padding: '2px 8px', border: '0.5px solid var(--color-border-strong)', borderRadius: 'var(--radius-sm)', background: 'var(--color-surface)', cursor: data?.rows.length ? 'pointer' : 'not-allowed' }}
        >
          Export CSV
        </button>
      </div>

      {isLoading ? <Spinner /> : !data || !data.rows.length ? (
        <EmptyState message="No worklogs in this window." />
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <WeeklyPivotTable pivot={pivot!} urlFor={urlFor} />
        </div>
      )}
    </div>
  );
}

interface PivotedDeliverable {
  deliverable_jira_issue_id: string;
  jira_issue_key: string;
  issue_type: 'Deliverable' | 'Overhead';
  deliverable_summary: string;
  deliverable_status: string;
  billable_rate: number;
  byWeek: Record<string, { hours: number; value: number }>;
  total: { hours: number; value: number };
}

interface PivotedEngagement {
  engagement_jira_issue_id: string | null;
  engagement_summary: string | null;
  deliverables: PivotedDeliverable[];
  byWeek: Record<string, { hours: number; value: number }>;
  total: { hours: number; value: number };
}

interface PivotedData {
  weekStarts: string[];
  engagements: PivotedEngagement[];
  byWeek: Record<string, { hours: number; value: number }>;
  grandTotal: { hours: number; value: number };
}

function pivotWeekly(rows: WeeklyScorecardRow[], weekStarts: string[]): PivotedData {
  const engMap = new Map<string, PivotedEngagement>();
  for (const r of rows) {
    const engKey = r.engagement_jira_issue_id ?? '__none__';
    let eng = engMap.get(engKey);
    if (!eng) {
      eng = {
        engagement_jira_issue_id: r.engagement_jira_issue_id,
        engagement_summary: r.engagement_summary,
        deliverables: [],
        byWeek: {},
        total: { hours: 0, value: 0 },
      };
      engMap.set(engKey, eng);
    }
    let deliv = eng.deliverables.find((d) => d.deliverable_jira_issue_id === r.deliverable_jira_issue_id);
    if (!deliv) {
      deliv = {
        deliverable_jira_issue_id: r.deliverable_jira_issue_id,
        jira_issue_key: r.jira_issue_key,
        issue_type: r.issue_type,
        deliverable_summary: r.deliverable_summary,
        deliverable_status: r.deliverable_status,
        billable_rate: Number(r.billable_rate),
        byWeek: {},
        total: { hours: 0, value: 0 },
      };
      eng.deliverables.push(deliv);
    }
    deliv.byWeek[r.week_start] = {
      hours: (deliv.byWeek[r.week_start]?.hours ?? 0) + Number(r.hours),
      value: (deliv.byWeek[r.week_start]?.value ?? 0) + Number(r.value),
    };
    deliv.total.hours += Number(r.hours);
    deliv.total.value += Number(r.value);
    eng.byWeek[r.week_start] = {
      hours: (eng.byWeek[r.week_start]?.hours ?? 0) + Number(r.hours),
      value: (eng.byWeek[r.week_start]?.value ?? 0) + Number(r.value),
    };
    eng.total.hours += Number(r.hours);
    eng.total.value += Number(r.value);
  }

  const byWeek: Record<string, { hours: number; value: number }> = {};
  const grand = { hours: 0, value: 0 };
  for (const w of weekStarts) {
    byWeek[w] = { hours: 0, value: 0 };
  }
  for (const eng of engMap.values()) {
    for (const [w, v] of Object.entries(eng.byWeek)) {
      byWeek[w] = {
        hours: (byWeek[w]?.hours ?? 0) + v.hours,
        value: (byWeek[w]?.value ?? 0) + v.value,
      };
    }
    grand.hours += eng.total.hours;
    grand.value += eng.total.value;
  }

  return {
    weekStarts,
    engagements: Array.from(engMap.values()).sort((a, b) =>
      (a.engagement_summary ?? 'zzz').localeCompare(b.engagement_summary ?? 'zzz')
    ),
    byWeek,
    grandTotal: grand,
  };
}

function fmtWeek(isoDate: string): string {
  // "2026-03-30" → "Mar 30". UTC-safe; date is a pure day label.
  const [y, m, d] = isoDate.split('-').map((n) => parseInt(n));
  return new Date(Date.UTC(y, m - 1, d)).toLocaleString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function WeeklyPivotTable({
  pivot, urlFor,
}: {
  pivot: PivotedData;
  urlFor: (key: string) => string | null;
}) {
  return (
    <Table>
      <thead>
        <tr>
          <Th>Deliverable</Th>
          {pivot.weekStarts.map((w) => (
            <Th key={w} right>{fmtWeek(w)}</Th>
          ))}
          <Th right>Total</Th>
        </tr>
      </thead>
      <tbody>
        {pivot.engagements.map((eng) => (
          <EngagementWeeklyGroup
            key={eng.engagement_jira_issue_id ?? '__none__'}
            eng={eng}
            weekStarts={pivot.weekStarts}
            urlFor={urlFor}
          />
        ))}
        <tr style={{ borderTop: '0.5px solid var(--color-border-strong)', background: 'var(--color-border)' }}>
          <td style={{ padding: '9px 16px', fontWeight: 500, fontSize: 12 }}>Grand total</td>
          {pivot.weekStarts.map((w) => (
            <td key={w} style={{ padding: '9px 16px', textAlign: 'right', fontWeight: 500, fontSize: 12 }}>
              {pivot.byWeek[w]?.value ? fmtCurrency(pivot.byWeek[w]!.value) : '—'}
            </td>
          ))}
          <td style={{ padding: '9px 16px', textAlign: 'right', fontWeight: 500, fontSize: 12, color: 'var(--color-green)' }}>
            {fmtCurrency(pivot.grandTotal.value)}
          </td>
        </tr>
      </tbody>
    </Table>
  );
}

function EngagementWeeklyGroup({
  eng, weekStarts, urlFor,
}: {
  eng: PivotedEngagement;
  weekStarts: string[];
  urlFor: (key: string) => string | null;
}) {
  const [collapsed, setCollapsed] = useState(false);
  return (
    <>
      <tr
        style={{ background: 'var(--color-border)', cursor: 'pointer' }}
        onClick={() => setCollapsed((c) => !c)}
      >
        <td style={{ padding: '7px 16px', fontSize: 11, fontWeight: 500 }}>
          <span style={{ display: 'inline-block', width: 12, color: 'var(--color-text-muted)' }}>{collapsed ? '▸' : '▾'}</span>
          {eng.engagement_summary ?? '(no engagement)'}
        </td>
        {weekStarts.map((w) => (
          <td key={w} style={{ padding: '7px 16px', textAlign: 'right', fontSize: 11, fontWeight: 500, color: 'var(--color-text-muted)' }}>
            {eng.byWeek[w]?.value ? fmtCurrency(eng.byWeek[w]!.value) : ''}
          </td>
        ))}
        <td style={{ padding: '7px 16px', textAlign: 'right', fontSize: 11, fontWeight: 500 }}>
          {fmtCurrency(eng.total.value)}
        </td>
      </tr>
      {!collapsed && eng.deliverables.map((d) => {
        const url = urlFor(d.jira_issue_key);
        return (
        <tr key={d.deliverable_jira_issue_id} style={{ borderBottom: '0.5px solid var(--color-border)' }}>
          <td style={{ padding: '8px 16px 8px 32px', fontSize: 12 }}>
            <IssueTypeBadge issueType={d.issue_type} />
            {url ? (
              <a href={url} target="_blank" rel="noreferrer" style={{ color: 'var(--color-blue)', fontSize: 11, marginRight: 6 }}>
                {d.jira_issue_key}
              </a>
            ) : (
              <span style={{ color: 'var(--color-text-muted)', fontSize: 11, marginRight: 6 }}>{d.jira_issue_key}</span>
            )}
            {d.deliverable_summary}
            {d.deliverable_status !== 'Completed' && (
              <span style={{ marginLeft: 8 }}>
                <Badge variant="amber">{d.deliverable_status}</Badge>
              </span>
            )}
          </td>
          {weekStarts.map((w) => (
            <td key={w} style={{ padding: '8px 16px', textAlign: 'right', fontSize: 12 }}>
              {d.byWeek[w]?.value ? fmtCurrency(d.byWeek[w]!.value) : ''}
            </td>
          ))}
          <td style={{ padding: '8px 16px', textAlign: 'right', fontSize: 12, fontWeight: 500 }}>
            {fmtCurrency(d.total.value)}
          </td>
        </tr>
        );
      })}
    </>
  );
}
